import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/utils/cn";
import { useMemo } from "react";
import { ADVISOR_GROUPS, resolveAdvisorCopy } from "./advisor.data";
import { AdvisorOption } from "./components/AdvisorOption";
import type { IAdvisor } from "./types";

interface AdvisorPickerProps {
  advisor: IAdvisor;
  onSelect: (slug: string) => void;
  disabled?: boolean;
  /** Resolves each row's copy (My Data Only vs Company Only, etc.) for the
   *  employee-facing surface. */
  isPersonal?: boolean;
  className?: string;
}

const resolveGroups = (
  isPersonal: boolean,
): { label: string; advisors: IAdvisor[] }[] => {
  const resolve = (advisors: IAdvisor[]) =>
    advisors.map((a) => resolveAdvisorCopy(a, isPersonal));
  return [
    { label: "Your data only", advisors: resolve(ADVISOR_GROUPS.internal) },
    {
      label: "Web-enabled · approved sources",
      advisors: resolve(ADVISOR_GROUPS.web),
    },
    { label: "Not available to you", advisors: resolve(ADVISOR_GROUPS.locked) },
  ];
};

/**
 * Advisor selector — an icon-only trigger beside the mic, the way a model
 * picker sits in a composer. The glyph is the current advisor, so the active
 * choice reads at a glance; the tooltip and aria-label name it.
 */
export const AdvisorPicker = ({
  advisor,
  onSelect,
  disabled,
  isPersonal = false,
  className,
}: AdvisorPickerProps) => {
  const { t } = useTranslation(["common"]);
  const Icon = advisor.icon;
  const groups = useMemo(() => resolveGroups(isPersonal), [isPersonal]);

  return (
    <DropdownMenu>
      <TooltipProvider delayDuration={200}>
        <Tooltip>
          <TooltipTrigger asChild>
            <DropdownMenuTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size="icon-sm"
                disabled={disabled}
                aria-label={t("common:ai_assistant.advisor_change_aria", {
                  name: t(advisor.name),
                })}
                className={cn(
                  "size-9 shrink-0 rounded text-muted-foreground hover:bg-muted",
                  advisor.webEnabled &&
                    "bg-info-muted text-info hover:bg-info-muted",
                  className,
                )}
              >
                <Icon className="size-5" />
              </Button>
            </DropdownMenuTrigger>
          </TooltipTrigger>
          <TooltipContent side="top" className="text-xs">
            {t("common:ai_assistant.advisor_label", { name: t(advisor.name) })}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>

      <DropdownMenuContent
        align="end"
        side="top"
        sideOffset={8}
        className="w-95 max-w-[calc(100vw-2rem)] p-1.5"
      >
        <DropdownMenuLabel className="flex items-center justify-between text-[11px] font-bold uppercase tracking-wider">
          {t("common:ai_assistant.advisor")}
          <span className="font-medium normal-case tracking-normal text-muted-foreground">
            {t("common:ai_assistant.applies_to_new_messages")}
          </span>
        </DropdownMenuLabel>

        {groups
          .filter((group) => group.advisors.length > 0)
          .map((group) => (
            <div key={group.label}>
              <DropdownMenuLabel className="pt-2 text-[10px] uppercase tracking-wider">
                {group.label}
              </DropdownMenuLabel>
              {group.advisors.map((option) => (
                <AdvisorOption
                  key={option.slug}
                  advisor={option}
                  isActive={option.slug === advisor.slug}
                  onSelect={onSelect}
                />
              ))}
            </div>
          ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
